import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { CreditCard, Plus, Edit2, Trash2, CalendarClock, Loader2, Users, Crown, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

export default function HesapAdminSubscriptionsPage() {
  const { token } = useHesapAuth();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [assignDialog, setAssignDialog] = useState(null); // {user_id, full_name}
  const [assignPlan, setAssignPlan] = useState('');
  const [assignDays, setAssignDays] = useState('');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        fetch(`${API}/subscription/plans`, { headers }).then(r => r.json()),
        fetch(`${API}/admin/subscriptions`, { headers }).then(r => r.json()),
      ]);
      setPlans(p); setSubs(s);
    } catch { toast.error('Yüklenemedi'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const savePlan = async () => {
    if (!editingPlan) return;
    const isNew = !editingPlan.id || editingPlan.__new;
    const url = isNew ? `${API}/subscription/plans` : `${API}/subscription/plans/${editingPlan.id}`;
    const method = isNew ? 'POST' : 'PUT';
    try {
      const r = await fetch(url, { method, headers, body: JSON.stringify({
        name: editingPlan.name,
        duration_days: Number(editingPlan.duration_days) || 30,
        price: Number(editingPlan.price) || 0,
        currency: editingPlan.currency || 'TL',
        active: editingPlan.active !== false,
        order: Number(editingPlan.order) || 99,
      }) });
      if (!r.ok) throw new Error();
      toast.success('Kaydedildi');
      setEditingPlan(null);
      load();
    } catch { toast.error('Hata'); }
  };

  const deletePlan = async (id) => {
    if (!window.confirm('Bu plan silinsin mi?')) return;
    try {
      await fetch(`${API}/subscription/plans/${id}`, { method: 'DELETE', headers });
      toast.success('Plan silindi'); load();
    } catch { toast.error('Hata'); }
  };

  const assignToUser = async () => {
    if (!assignDialog) return;
    const body = {};
    if (assignDays) {
      body.extend_days = Number(assignDays);
      if (assignPlan) body.plan_id = assignPlan;
    } else if (assignPlan) {
      body.plan_id = assignPlan;
    } else {
      toast.error('Plan veya gün belirtin'); return;
    }
    try {
      const r = await fetch(`${API}/admin/subscriptions/${assignDialog.user_id}`, {
        method: 'PUT', headers, body: JSON.stringify(body)
      });
      if (!r.ok) throw new Error();
      toast.success('Abonelik güncellendi');
      setAssignDialog(null); setAssignPlan(''); setAssignDays('');
      load();
    } catch { toast.error('Hata'); }
  };

  const filteredSubs = subs.filter(s => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (s.full_name || '').toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q);
  });

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="admin-subscriptions-page">
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white shadow-md">
          <CreditCard className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Abonelik Yönetimi</h1>
          <p className="text-sm text-slate-500">Planlar ve kullanıcı abonelikleri</p>
        </div>
      </div>

      {/* Planlar */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Crown className="h-4 w-4 text-amber-600" />Planlar
          </CardTitle>
          <Button onClick={() => setEditingPlan({ __new: true, name: '', duration_days: 30, price: 0, currency: 'TL', active: true, order: plans.length + 1 })}
            size="sm" className="bg-violet-600 hover:bg-violet-700" data-testid="add-plan-btn">
            <Plus className="h-4 w-4 mr-1" />Yeni Plan
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {plans.map(p => (
              <div key={p.id} className="border-2 border-slate-200 rounded-xl p-3 hover:border-violet-300 transition bg-white"
                data-testid={`plan-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="font-bold">{p.name}</p>
                  <div className="flex gap-1">
                    <button onClick={() => setEditingPlan({ ...p })}
                      className="p-1 hover:bg-slate-100 rounded text-slate-500">
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => deletePlan(p.id)}
                      className="p-1 hover:bg-red-100 rounded text-red-500">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <p className="text-2xl font-black text-violet-700">
                  {p.price === 0 ? 'Ücretsiz' : `${p.price} ${p.currency || 'TL'}`}
                </p>
                <p className="text-xs text-slate-500 mt-1">{p.duration_days} gün</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Kullanıcı Abonelikleri */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-sky-600" />
            Kullanıcı Abonelikleri ({subs.length})
          </CardTitle>
          <Input placeholder="Kullanıcı ara (ad, e-posta)..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="mt-2" data-testid="admin-subs-search" />
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {filteredSubs.length === 0 && (
              <div className="text-center text-slate-400 py-8 text-sm">Sonuç yok</div>
            )}
            {filteredSubs.map(s => {
              const planName = plans.find(p => p.id === s.plan_id)?.name || '—';
              const stateColor = s.active ? 'bg-emerald-100 text-emerald-700'
                              : s.expired ? 'bg-red-100 text-red-700'
                              : 'bg-amber-100 text-amber-700';
              const stateLabel = s.active ? `Aktif · ${s.days_left}g`
                              : s.expired ? 'Süresi Doldu'
                              : 'Abonelik Yok';
              const stateIcon = s.active ? CheckCircle2 : AlertTriangle;
              const StateIcon = stateIcon;
              return (
                <div key={s.user_id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition"
                  data-testid={`sub-row-${s.user_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate">{s.full_name}</p>
                    <p className="text-[11px] text-slate-500 truncate">{s.email}</p>
                  </div>
                  <div className="text-xs hidden sm:block">
                    <span className="text-slate-500">Plan: </span>
                    <b>{planName}</b>
                  </div>
                  <div className={`text-[11px] font-bold px-2.5 py-1 rounded flex items-center gap-1 ${stateColor}`}>
                    <StateIcon className="h-3 w-3" />
                    {stateLabel}
                  </div>
                  <Button onClick={() => { setAssignDialog(s); setAssignPlan(s.plan_id || ''); setAssignDays(''); }}
                    size="sm" variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50"
                    data-testid={`assign-sub-btn-${s.user_id}`}>
                    <CalendarClock className="h-3.5 w-3.5 mr-1" />Düzenle
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Plan düzenle/ekle dialog */}
      <Dialog open={!!editingPlan} onOpenChange={(o) => !o && setEditingPlan(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPlan?.__new ? 'Yeni Plan' : 'Plan Düzenle'}</DialogTitle>
          </DialogHeader>
          {editingPlan && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Plan Adı</Label>
                <Input value={editingPlan.name || ''} onChange={e => setEditingPlan({ ...editingPlan, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Gün</Label>
                  <Input type="number" value={editingPlan.duration_days} onChange={e => setEditingPlan({ ...editingPlan, duration_days: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Fiyat (TL)</Label>
                  <Input type="number" step="0.01" value={editingPlan.price} onChange={e => setEditingPlan({ ...editingPlan, price: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Sıra</Label>
                  <Input type="number" value={editingPlan.order || 99} onChange={e => setEditingPlan({ ...editingPlan, order: e.target.value })} />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={editingPlan.active !== false} onChange={e => setEditingPlan({ ...editingPlan, active: e.target.checked })} />
                    Aktif
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditingPlan(null)}>İptal</Button>
                <Button onClick={savePlan} className="bg-violet-600 hover:bg-violet-700" data-testid="save-plan-btn">Kaydet</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Kullanıcıya plan atama dialog */}
      <Dialog open={!!assignDialog} onOpenChange={(o) => !o && setAssignDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Abonelik Düzenle — {assignDialog?.full_name}</DialogTitle>
          </DialogHeader>
          {assignDialog && (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded p-2 text-xs">
                <b>{assignDialog.email}</b><br/>
                Mevcut durum: {assignDialog.active ? `${assignDialog.days_left} gün kaldı` : (assignDialog.expired ? 'Süresi doldu' : 'Aktif değil')}
              </div>
              <div>
                <Label className="text-xs">Plan Seç</Label>
                <select value={assignPlan} onChange={e => setAssignPlan(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                  data-testid="assign-plan-select">
                  <option value="">— Plan seçin —</option>
                  {plans.map(p => <option key={p.id} value={p.id}>{p.name} ({p.duration_days} gün)</option>)}
                </select>
                <p className="text-[10px] text-slate-500 mt-1">Plan seçilirse o planın gün sayısı atanır.</p>
              </div>
              <div>
                <Label className="text-xs">Veya: Bugünden itibaren X gün</Label>
                <Input type="number" value={assignDays}
                  onChange={e => setAssignDays(e.target.value)}
                  placeholder="Örn: 30" data-testid="assign-days-input" />
                <p className="text-[10px] text-slate-500 mt-1">Manuel uzatma için gün sayısı girin.</p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setAssignDialog(null)}>İptal</Button>
                <Button onClick={assignToUser} className="bg-violet-600 hover:bg-violet-700" data-testid="confirm-assign-btn">
                  Kaydet
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
